import React, { useRef, useState } from "react";

import {
  addKeyframe,
  KEYFRAME_EASES,
  parseBezier,
  removeKeyframe,
  sampleTrack,
  TRACK_CHANNELS,
  updateKeyframe,
} from "../animation/tracks";

import "./KeyframeEditor.scss";

import type { ClipTracks, TrackChannel } from "../animation/tracks";

const GRAPH_W = 560;
const GRAPH_H = 220;
const PAD = 14;
const CURVE_SAMPLES = 64;

/**
 * Premium curve editor for a `keyframed` clip's tracks. Pure view over
 * animation/tracks.ts ops — every interaction routes through `onChange`, so
 * undo/redo and live recompile ride the clip-update path the rest of the
 * settings UI already uses.
 */
export const KeyframeEditor = ({
  tracks,
  durationMs,
  onChange,
  onClose,
  channels = TRACK_CHANNELS,
}: {
  tracks: ClipTracks;
  durationMs: number;
  onChange: (tracks: ClipTracks) => void;
  onClose: () => void;
  /** channel registry to edit against — defaults to the element tracks;
   * the keyframed camera passes CAMERA_TRACK_CHANNELS */
  channels?: readonly TrackChannel[];
}) => {
  const channelOf = (key: string) => channels.find((c) => c.key === key);
  const activeChannels = Object.keys(tracks);
  const [activeKey, setActiveKey] = useState<string | null>(
    activeChannels[0] ?? null,
  );
  const [selected, setSelected] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // during a drag: [channel, index] — pointer capture keeps fast drags alive
  const dragRef = useRef<{ channel: string; index: number } | null>(null);
  // dragging a bezier tangent handle (1 = outgoing, 2 = incoming)
  const bezierDragRef = useRef<{
    channel: string;
    index: number;
    which: 1 | 2;
  } | null>(null);

  const active =
    activeKey && tracks[activeKey] ? activeKey : activeChannels[0] ?? null;
  const activeCh = active ? channelOf(active) : undefined;
  const activeKfs = active ? tracks[active] : undefined;

  const inactive = channels.filter((c) => !tracks[c.key]);

  // value ↔ pixel mapping for a channel (its full UI range fills the graph)
  const toX = (t: number) => PAD + t * (GRAPH_W - PAD * 2);
  const toY = (ch: NonNullable<typeof activeCh>, v: number) =>
    GRAPH_H - PAD - ((v - ch.min) / (ch.max - ch.min)) * (GRAPH_H - PAD * 2);
  const fromPoint = (clientX: number, clientY: number) => {
    const rect = svgRef.current!.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * GRAPH_W;
    const y = ((clientY - rect.top) / rect.height) * GRAPH_H;
    const t = Math.min(1, Math.max(0, (x - PAD) / (GRAPH_W - PAD * 2)));
    const frac = Math.min(
      1,
      Math.max(0, (GRAPH_H - PAD - y) / (GRAPH_H - PAD * 2)),
    );
    return { t, frac };
  };

  const curvePath = (key: string) => {
    const ch = channelOf(key);
    const kfs = tracks[key];
    if (!ch || !kfs || kfs.length === 0) {
      return "";
    }
    let d = "";
    for (let i = 0; i <= CURVE_SAMPLES; i++) {
      const t = i / CURVE_SAMPLES;
      const x = toX(t);
      const y = toY(ch, sampleTrack(kfs, t));
      d += `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)} `;
    }
    return d;
  };

  const commit = (next: ClipTracks) => onChange(next);

  const handleGraphDoubleClick = (e: React.MouseEvent) => {
    if (!active || !activeCh) {
      return;
    }
    const { t, frac } = fromPoint(e.clientX, e.clientY);
    const v = activeCh.min + frac * (activeCh.max - activeCh.min);
    const snapped = Math.round(t * 100) / 100;
    commit(addKeyframe(tracks, active, snapped, v));
    setSelected(null);
  };

  const handleHandlePointerDown = (index: number, e: React.PointerEvent) => {
    if (!active) {
      return;
    }
    e.stopPropagation();
    // optional-chained: jsdom (tests) has no pointer capture
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { channel: active, index };
    setSelected(index);
  };

  const handleHandlePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || !activeCh) {
      return;
    }
    const { t, frac } = fromPoint(e.clientX, e.clientY);
    const v = activeCh.min + frac * (activeCh.max - activeCh.min);
    const next = updateKeyframe(tracks, drag.channel, drag.index, {
      t: Math.round(t * 100) / 100,
      v,
    });
    // the keyframe may have re-sorted — track it by its new time
    const moved = next[drag.channel].findIndex(
      (k) => Math.abs(k.t - Math.round(t * 100) / 100) < 0.005,
    );
    if (moved >= 0) {
      dragRef.current = { channel: drag.channel, index: moved };
      setSelected(moved);
    }
    commit(next);
  };

  const handleHandlePointerUp = () => {
    dragRef.current = null;
  };

  const selectedKf =
    active && activeKfs && selected !== null ? activeKfs[selected] : null;
  // bezier segment editing: the selected key's incoming segment, when its
  // ease is a bezier and a previous key exists to anchor the tangents
  const selectedBezier =
    selectedKf?.ease && selected !== null && selected > 0
      ? parseBezier(selectedKf.ease)
      : null;
  const bezierPrevKf =
    selectedBezier && activeKfs && selected !== null
      ? activeKfs[selected - 1]
      : null;

  const handleBezierPointerMove = (e: React.PointerEvent) => {
    const drag = bezierDragRef.current;
    if (!drag || !activeCh || !bezierPrevKf || !selectedKf || !selectedBezier) {
      return;
    }
    const { t, frac } = fromPoint(e.clientX, e.clientY);
    const v = activeCh.min + frac * (activeCh.max - activeCh.min);
    const dt = selectedKf.t - bezierPrevKf.t || 1e-6;
    const dv = selectedKf.v - bezierPrevKf.v;
    const nx = Math.min(1, Math.max(0, (t - bezierPrevKf.t) / dt));
    const ny =
      dv === 0
        ? // flat segment: map the vertical drag over ±half the channel range
          Math.min(
            2,
            Math.max(
              -1,
              (v - bezierPrevKf.v) / ((activeCh.max - activeCh.min) / 2),
            ),
          )
        : Math.min(2, Math.max(-1, (v - bezierPrevKf.v) / dv));
    const [x1, y1, x2, y2] = selectedBezier;
    const round = (n: number) => Math.round(n * 100) / 100;
    const next =
      drag.which === 1
        ? `bezier(${round(nx)},${round(ny)},${round(x2)},${round(y2)})`
        : `bezier(${round(x1)},${round(y1)},${round(nx)},${round(ny)})`;
    commit(updateKeyframe(tracks, drag.channel, drag.index, { ease: next }));
  };

  return (
    <div className="keyframe-editor" data-testid="keyframe-editor">
      <div className="keyframe-editor__header">
        <span className="keyframe-editor__title">Curves</span>
        <select
          data-testid="kf-add-channel"
          className="keyframe-editor__add"
          value=""
          onChange={(e) => {
            const key = e.target.value;
            const ch = channelOf(key);
            if (!ch) {
              return;
            }
            commit({
              ...tracks,
              [key]: [
                { t: 0, v: ch.neutral },
                { t: 1, v: ch.neutral === ch.min ? ch.max : ch.min },
              ],
            });
            setActiveKey(key);
            setSelected(null);
          }}
        >
          <option value="" disabled>
            + Add channel
          </option>
          {inactive.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
        <button
          className="keyframe-editor__close"
          onClick={onClose}
          title="Close curve editor"
        >
          ×
        </button>
      </div>

      <div className="keyframe-editor__chips">
        {activeChannels.map((key) => {
          const ch = channelOf(key);
          return (
            <span
              key={key}
              className={`keyframe-editor__chip${
                key === active ? " keyframe-editor__chip--active" : ""
              }`}
              onClick={() => {
                setActiveKey(key);
                setSelected(null);
              }}
            >
              <i
                className="keyframe-editor__dot"
                style={{ background: ch?.color }}
              />
              {ch?.label ?? key}
              <button
                data-testid={`kf-remove-${key}`}
                className="keyframe-editor__chip-x"
                title={`Remove the ${ch?.label ?? key} track`}
                onClick={(e) => {
                  e.stopPropagation();
                  let next = tracks;
                  const count = tracks[key]?.length ?? 0;
                  for (let i = count - 1; i >= 0; i--) {
                    next = removeKeyframe(next, key, i);
                  }
                  commit(next);
                  if (active === key) {
                    setActiveKey(null);
                    setSelected(null);
                  }
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        {activeChannels.length === 0 && (
          <span className="keyframe-editor__empty">
            Add a channel to start animating
          </span>
        )}
      </div>

      <svg
        ref={svgRef}
        className="keyframe-editor__graph"
        viewBox={`0 0 ${GRAPH_W} ${GRAPH_H}`}
        onDoubleClick={handleGraphDoubleClick}
      >
        {/* grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={`v${f}`}
            className="keyframe-editor__grid"
            x1={toX(f)}
            y1={PAD}
            x2={toX(f)}
            y2={GRAPH_H - PAD}
          />
        ))}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={`h${f}`}
            className="keyframe-editor__grid"
            x1={PAD}
            y1={PAD + f * (GRAPH_H - PAD * 2)}
            x2={GRAPH_W - PAD}
            y2={PAD + f * (GRAPH_H - PAD * 2)}
          />
        ))}
        <rect
          className="keyframe-editor__frame"
          x={PAD}
          y={PAD}
          width={GRAPH_W - PAD * 2}
          height={GRAPH_H - PAD * 2}
        />
        {/* every channel's curve, active on top */}
        {activeChannels
          .filter((key) => key !== active)
          .map((key) => (
            <path
              key={key}
              className="keyframe-editor__curve keyframe-editor__curve--dim"
              d={curvePath(key)}
              style={{ stroke: channelOf(key)?.color }}
            />
          ))}
        {active && (
          <path
            className="keyframe-editor__curve"
            d={curvePath(active)}
            style={{ stroke: activeCh?.color }}
          />
        )}
        {/* keyframe handles for the active channel */}
        {active &&
          activeCh &&
          activeKfs?.map((kf, i) => (
            <rect
              key={`${kf.t}-${i}`}
              data-testid="kf-handle"
              className={`keyframe-editor__handle${
                i === selected ? " keyframe-editor__handle--selected" : ""
              }`}
              x={toX(kf.t) - 5}
              y={toY(activeCh, kf.v) - 5}
              width={10}
              height={10}
              transform={`rotate(45 ${toX(kf.t)} ${toY(activeCh, kf.v)})`}
              onPointerDown={(e) => handleHandlePointerDown(i, e)}
              onPointerMove={handleHandlePointerMove}
              onPointerUp={handleHandlePointerUp}
            />
          ))}
        {/* bezier tangent handles for the selected key's incoming segment */}
        {selectedBezier &&
          bezierPrevKf &&
          selectedKf &&
          activeCh &&
          active &&
          ([1, 2] as const).map((which) => {
            const [x1, y1, x2, y2] = selectedBezier;
            const bx = which === 1 ? x1 : x2;
            const by = which === 1 ? y1 : y2;
            const dt = selectedKf.t - bezierPrevKf.t;
            const dv = selectedKf.v - bezierPrevKf.v;
            const flatSpan = (activeCh.max - activeCh.min) / 2;
            const ht = bezierPrevKf.t + bx * dt;
            const hv = bezierPrevKf.v + by * (dv === 0 ? flatSpan : dv);
            const anchor = which === 1 ? bezierPrevKf : selectedKf;
            return (
              <g key={which}>
                <line
                  className="keyframe-editor__tangent"
                  x1={toX(anchor.t)}
                  y1={toY(activeCh, anchor.v)}
                  x2={toX(ht)}
                  y2={toY(activeCh, hv)}
                />
                <circle
                  data-testid="kf-bezier-handle"
                  className="keyframe-editor__bezier-handle"
                  cx={toX(ht)}
                  cy={toY(activeCh, hv)}
                  r={5}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    (e.currentTarget as Element).setPointerCapture?.(
                      e.pointerId,
                    );
                    bezierDragRef.current = {
                      channel: active,
                      index: selected!,
                      which,
                    };
                  }}
                  onPointerMove={handleBezierPointerMove}
                  onPointerUp={() => {
                    bezierDragRef.current = null;
                  }}
                />
              </g>
            );
          })}
        {/* time labels */}
        <text className="keyframe-editor__label" x={PAD} y={GRAPH_H - 2}>
          0
        </text>
        <text
          className="keyframe-editor__label"
          x={GRAPH_W - PAD}
          y={GRAPH_H - 2}
          textAnchor="end"
        >
          {durationMs} ms
        </text>
      </svg>

      {selectedKf && active && activeCh && (
        <div className="keyframe-editor__inspector">
          <label>
            Time
            <input
              type="number"
              min={0}
              max={durationMs}
              step={10}
              value={Math.round(selectedKf.t * durationMs)}
              onChange={(e) => {
                const ms = Number(e.target.value);
                if (Number.isFinite(ms)) {
                  commit(
                    updateKeyframe(tracks, active, selected!, {
                      t: ms / durationMs,
                    }),
                  );
                }
              }}
            />
          </label>
          <label>
            Value{activeCh.unit ? ` (${activeCh.unit})` : ""}
            <input
              type="number"
              min={activeCh.min}
              max={activeCh.max}
              step={activeCh.step}
              value={Math.round(selectedKf.v * 100) / 100}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) {
                  commit(updateKeyframe(tracks, active, selected!, { v }));
                }
              }}
            />
          </label>
          <label>
            Ease in
            <select
              data-testid="kf-ease"
              value={
                selectedBezier
                  ? "bezier(0.5,0,0.5,1)"
                  : selectedKf.ease ?? "linear"
              }
              onChange={(e) =>
                commit(
                  updateKeyframe(tracks, active, selected!, {
                    ease:
                      e.target.value === "linear" ? undefined : e.target.value,
                  }),
                )
              }
            >
              {KEYFRAME_EASES.map((ease) => (
                <option key={ease} value={ease}>
                  {ease.startsWith("bezier(") ? "bezier…" : ease}
                </option>
              ))}
            </select>
          </label>
          <button
            data-testid="kf-delete"
            className="keyframe-editor__delete"
            title="Delete this keyframe"
            onClick={() => {
              commit(removeKeyframe(tracks, active, selected!));
              setSelected(null);
            }}
          >
            Delete
          </button>
        </div>
      )}
      <p className="keyframe-editor__hint">
        Double-click the graph to add a key · drag keys to move · click a key to
        edit its ease
      </p>
    </div>
  );
};
